import { keyframes } from '@emotion/react';
import styled from '@emotion/styled';

import {
	colors,
	font,
	headingFont,
	mq,
	mqMax,
	pageGutter,
	radii,
	shadows,
	spacing,
} from '@/lib/theme';

/*
 * The "full card" layout on phones, used by the check-in pages and the
 * property page: a photo edge to edge at the top that stays put, the page's
 * controls over its top corner, and the content as one white panel rising
 * over it. From tablets up, pages keep their header and their own layout.
 */

/** The site header, from tablets up; phones show `CoverActions` instead. */
export const WideScreenOnly = styled.div`
	display: none;

	${mq.md} {
		display: block;
	}
`;

const fadeOut = keyframes`
	to {
		opacity: 0;
		visibility: hidden;
	}
`;

/** The header's controls over the photo's top corner (phones), fading as the page scrolls. */
export const CoverActions = styled.div`
	position: sticky;
	top: 0;
	z-index: 2;
	height: 0;
	display: flex;
	/* Not stretched to the strip's zero height, which would centre the pill above it. */
	align-items: flex-start;
	justify-content: flex-end;

	& > * {
		margin-top: ${spacing.space40};
	}

	@supports (animation-timeline: scroll()) {
		animation: ${fadeOut} linear both;
		animation-timeline: scroll(nearest);
		animation-range: 0 80px;
	}

	${mq.md} {
		display: none;
	}
`;

/** Pins a photo edge to edge at the top on phones, under the rising panel. */
export const coverPhoto = `
	${mqMax.md} {
		position: sticky;
		top: 0;
		margin: 0 -${pageGutter.mobile};
		border-radius: 0;
	}
`;

/** How far the panel rises over the photo, so anything at the photo's foot can clear it. */
export const coverOverlap = spacing.space50;

/** The page's content as one white panel rising over the photo (phones). */
export const CoverSheet = styled.section`
	position: relative;
	z-index: 1;

	${mqMax.md} {
		margin: -${coverOverlap} -${pageGutter.mobile} 0;
		overflow: clip;
		border-radius: ${radii.radii50} ${radii.radii50} 0 0;
		background: ${colors.WH100};
		box-shadow: 0 -12px 24px -12px rgba(15, 23, 42, 0.16);
	}
`;

/* Phones: the photo pinned at the top, under the rising panel. */
export const CoverHero = styled.div<{ $hasPhoto: boolean }>`
	position: sticky;
	top: 0;
	aspect-ratio: 4 / 3;
	margin: 0 -${pageGutter.mobile};
	/* clip, not hidden: hidden would make this the scroller the name's fade follows. */
	overflow: clip;
	background: ${({ $hasPhoto }) => ($hasPhoto ? colors.LM_NU20 : colors.BB10)};

	img {
		position: absolute;
		inset: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
	}

	/* Darkens the bottom of the photo so the name reads on any picture. */
	&::after {
		content: '';
		position: absolute;
		inset: 0;
		background: linear-gradient(
			to top,
			${colors.LM_NU90T} 0%,
			${colors.LM_NU10T} 55%,
			transparent 100%
		);
	}

	${mq.md} {
		position: relative;
		aspect-ratio: 2 / 1;
		margin: ${spacing.space50} 0 0;
		border-radius: ${radii.radii50};
	}
`;

/** The page as one panel: rising over the photo on phones, a card below it from tablets up. */
export const CoverPanel = styled(CoverSheet)`
	${mq.md} {
		margin: ${spacing.space50} 0 0;
		overflow: clip;
		border-radius: ${radii.radii50};
		background: ${colors.WH100};
		box-shadow: ${shadows.shadow40};
	}
`;

/* One part of the panel, set off from the one above by a rule. */
export const CoverSection = styled.section`
	display: flex;
	flex-direction: column;
	gap: ${spacing.space40};
	padding: ${spacing.space50};
	border-top: 1px solid ${colors.interface.border};

	${mq.md} {
		padding: ${spacing.space60};
	}
`;

/** The place's name over the foot of the photo, fading as the panel rises over it. */
export const CoverCaption = styled.div`
	position: absolute;
	z-index: 1;
	left: 0;
	right: 0;
	bottom: 0;
	display: flex;
	flex-direction: column;
	gap: ${spacing.space10};
	/* Clear of the panel's top edge, which overlaps the photo on phones. */
	padding: ${spacing.space50} ${spacing.space50}
		calc(${spacing.space50} + ${spacing.space50});
	color: ${colors.WH100};

	/* Phones: the name fades as the panel slides up over the photo. */
	@supports (animation-timeline: scroll()) {
		animation: ${fadeOut} linear both;
		animation-timeline: scroll(nearest);
		animation-range: 0 80px;
	}

	${mq.md} {
		padding: ${spacing.space60};
		animation: none;
	}
`;

/* A step below the page's title, so the page's own heading leads. */
export const CoverPlaceName = styled.span`
	${headingFont('RWB110')}
	text-wrap: balance;

	${mq.md} {
		${headingFont('RWB120')}
	}
`;

export const CoverPlaceWhere = styled.span`
	${font('IM90')}
	color: ${colors.WH70T};
`;
